"use client";

import React from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { MenuNode } from "./menuAdapter";
import { HEADER_V2_DESIGN_SYSTEM } from "./designTokens";
import { useFastRouter } from "@/packages/hooks/useFastRouter";

interface MenuItemProps {
  node: MenuNode;
  isActive: boolean;
  isHovered: boolean;
  onHover: () => void;
  onClick: () => void;
}

export default function MenuItem({
  node,
  isActive,
  isHovered,
  onHover,
  onClick,
}: MenuItemProps) {
  const hasChildren = node.children && node.children.length > 0;
  const { colors } = HEADER_V2_DESIGN_SYSTEM;
  const { fastPush } = useFastRouter();

  const handleItemClick = () => {
    if (!hasChildren) {
      onClick();
      fastPush(node.href);
    }
  };

  return (
    <div
      onPointerEnter={onHover}
      onClick={handleItemClick}
      className={`group relative flex items-center justify-between px-3 py-1 rounded-md text-[13px] transition-all duration-150 ease-out cursor-pointer ${
        isActive || isHovered
          ? `${colors.lightGrayBg} hover:bg-slate-100/70 text-[var(--brand-accent)] font-medium`
          : `${colors.primaryDarkText} font-medium hover:${colors.lightGrayBg} hover:text-[var(--brand-accent)]`
      }`}
    >
      <Link href={node.href} className="flex-grow text-left block">
        <div className="flex flex-col">
          <span className="capitalize">{node.label}</span>
          {node.description && (
            <span className="text-[11px] text-slate-400 font-normal mt-0.5 line-clamp-1">
              {node.description}
            </span>
          )}
        </div>
      </Link>

      {/* Extensible: optional badge render */}
      {node.badge && (
        <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-800 rounded uppercase tracking-wide shrink-0">
          {node.badge}
        </span>
      )}

      {/* Sub-children Chevron indicator */}
      {hasChildren && (
        <ChevronRight className={`w-[11px] h-[11px] ml-2 transition-colors shrink-0 ${
          isActive || isHovered ? "text-[var(--brand-accent)]" : "text-slate-300 group-hover:text-[var(--brand-accent)]"
        }`} />
      )}
    </div>
  );
}
